/**
 * TODO IMPORTANT
 *
 * - focus on open
 * - trap focus inside modale
 */
import type { AfterViewInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  output,
  Renderer2,
  viewChild,
} from '@angular/core';
import { Subject, tap } from 'rxjs';

import { ValueOf } from '../../types/base/';
import { ButtonComponent } from '../button/button.component';
import { ModalService } from './modal.service';

export const errorTypes = {
  CANCEL: 'cancelled',
} as const;
export type ErrorType = ValueOf<typeof errorTypes>;

export const modalTypes = {
  /** default value */
  DEFAULT: 'default',
  SCROLL_CONTENT: 'scroll-content',
} as const;
export type ModalType = ValueOf<typeof modalTypes>;

export type ModalSubjectOptions = {
  /** subject will pipe modal closure */
  closeOnConfirm: true | boolean;
  /** subject will not emit error on close (cancel) */
  catchOnCancel: true | boolean;
};

@Component({
  selector: 'evc-modal',
  imports: [ButtonComponent],
  templateUrl: './modal.component.html',
  styleUrls: ['./modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:keydown.escape)': 'close()',
  },
})
export class ModalComponent implements AfterViewInit {
  // eslint-disable-next-line @angular-eslint/no-output-native, @angular-eslint/no-output-rename
  readonly closeModal = output({ alias: 'close' });

  readonly showBackdrop = input(true);
  readonly showReduceButton = input(true);
  readonly showCloseButton = input(true);
  readonly styles = input<Record<string, string> | undefined>(undefined);
  readonly type = input<ModalType>(modalTypes.DEFAULT);
  readonly options = input<Partial<ModalSubjectOptions>>({});
  #options = computed<ModalSubjectOptions>(() => ({
    closeOnConfirm: true,
    catchOnCancel: true,
    ...this.options(),
  }));

  readonly modalContainer = viewChild<ElementRef>('modalContainer');
  readonly modalBackdrop = viewChild<ElementRef>('modalBackdrop');

  #modalService = inject(ModalService);
  readonly #renderer = inject(Renderer2);

  /** Emits on modal close.
   * allow to be chain with form submission, store actions etc
   * c.f. confirm modal `action.callback$().subscribe(this.subject);`
   * allow to chain modal confirmation within drive store effects
   */
  readonly subject = new Subject();

  constructor() {
    this.subject
      .pipe(
        tap(() => {
          if (this.#options().closeOnConfirm) this.close(true);
        }),
      )
      .subscribe();
  }

  close(submitted = false): void {
    this.closeModal.emit();
    this.#modalService.close();

    if (!submitted) {
      if (!this.#options().catchOnCancel) {
        this.subject.error(new Error(errorTypes.CANCEL));
      } else {
        this.subject.complete();
      }
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      if (!this.#renderer) return;

      const container = this.modalContainer()?.nativeElement;
      const backdrop = this.modalBackdrop()?.nativeElement;

      if (backdrop && this.showBackdrop()) {
        this.#renderer.addClass(backdrop, 'show');
      }
      if (container) {
        this.#renderer.addClass(container, 'show');
      }
    });
  }
}
